import { useState } from 'react';

import { useUserStore } from 'src/hooks/stores';

import {
    FloatingLabelContainer,
    FloatingLabelInput,
    FloatingLabel,
    ErrorMessage,
    FloatingLabelInputNumberFormat,
} from 'src/components/Inputs/FloatingLabel';
import { Button, Box, Heading, FormControl } from '@chakra-ui/react';
import { EllipsisLoading } from 'src/components/loadings';

import { putUser } from 'src/legacy/Components/auth/_api';
import { useForm } from 'react-hook-form';
import { yup, yupResolver } from 'src/utils/form';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthModal } from 'src/modules/auth-modal';
import { useToast } from 'src/hooks';

type LoginFormProps = {
    navigate: any;
};

type formSchema = {
    name: string;
    email: string;
    cellphone: string;
};

const formValidationSchema = yup.object({
    cellphone: yup.string().required('Digite seu número de celular').cellphone('Digite um número válido'),
    name: yup.string().required('Preencha o nome'),
});

const CompleteSingupForm: React.FC<LoginFormProps> = ({ navigate}) => {
    const toast = useToast();
    
    const { email, name, token, id, setUser } = useUserStore();
    const { onClose } = useAuthModal();
    const queryClient = useQueryClient();
    
    const [, setError] = useState<string | null>(null);
    const [newName, setNewName] = useState(name);
    const [newCellPhone, setNewCellPhone] = useState('');

    const {
        register,
        handleSubmit,
        formState: { isSubmitting, errors },
    } = useForm<formSchema>({
        resolver: yupResolver(formValidationSchema),
    });

    const { onBlur: onCellphoneChange, ...registerCellphone } = register('cellphone');
    const { onBlur: onNameChange, ...registerName } = register('name');
    
    const onSubmit = handleSubmit(async data => {
        try {
            if(errors.name || errors.cellphone) return
            setError(null)

            setUser({name, email, token, id, userStatus: 'preactive'})
            
            await putUser({name: newName, email: email, cellPhone: newCellPhone.replace(/[^\d]/g, '')}).then(() => {
                useUserStore.setState({
                    userStatus: 'active'
                })
            })
            
            onClose()
            queryClient.invalidateQueries({ queryKey: ['me'] });
        } catch (er: any) {
            setUser({name, email, token, id, userStatus: 'pending'})
            setError(er.data.message);
            toast({
                description: er.data.message,
            });
        }
    });

    return (
        <Box className="auth-section" mb='-18px'>
        <Heading mt="0">
            Completar cadastro
        </Heading>
        <Box as="form" data-testid="login-form" noValidate onSubmit={onSubmit}>
            <FormControl mb="4">
            <FloatingLabelContainer>
                <FloatingLabelInput
                type="text"
                value={newName}
                data-testid="login-input-name"
                {...registerName}
                onChange={e => {
                    onNameChange(e)
                    setNewName(e.target.value);
                    setError(null);
                }}
                />
                <FloatingLabel>Nome</FloatingLabel>
            </FloatingLabelContainer>
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
            </FormControl>

            <FormControl mb="4">
            <FloatingLabelContainer>
                <FloatingLabelInput
                    _disabled={{bg: "#F3F3F3", color: "#222", cursor: 'not-allowed'}}
                    value={email}
                    isDisabled
                />
                <FloatingLabel>Email</FloatingLabel>
            </FloatingLabelContainer>
            </FormControl>

            <FormControl mb="4">
            <FloatingLabelContainer>
                <FloatingLabelInputNumberFormat
                    type="tel"
                    format="(##) #####-####"
                    value={newCellPhone}
                    data-testid="login-input-cellphone"
                    {...registerCellphone}
                    onChange={e => {
                        onCellphoneChange(e);
                        setNewCellPhone(e.target.value)
                        setError(null);
                    }}
                />
                <FloatingLabel>Celular</FloatingLabel>
            </FloatingLabelContainer>
            <ErrorMessage>{errors.cellphone?.message}</ErrorMessage>
            </FormControl>

            <Button
                type="submit"
                mt="2"
                colorScheme="brand"
                rounded="full"
                w="full"
                isLoading={isSubmitting}
                spinner={<EllipsisLoading color="white" />}
            >
                Completar cadastro
            </Button>
        </Box>
        <Button
            textAlign="center"
            mt="10px"
            w="full"
            bg="transparent"
            textColor="black"
            color="black"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            onClick={() => navigate('login')}
        >
            Já tem conta?{' '}
            <Box as="span" color="brand.500" ml="2">
                Login
            </Box>
        </Button>
    </Box>
    );
};

export default CompleteSingupForm;
